import { UserSubscription } from '@griply/types';
import Dialog from '@mui/material/Dialog';
import { useDeleteAccount, useLogout } from 'features/user';
import React, { useCallback, useMemo } from 'react';

import { DeleteAccountConfirm } from './delete-account-confirm';
import { DeleteAccountError } from './delete-account-error';
import { DeleteAccountLoading } from './delete-account-loading';
import { DeleteAccountSubscriptionWarning } from './delete-account-subscription-warning';

export type DeleteAccountDialogProps = {
  open: boolean;
  blockingSubscription: UserSubscription | null;
  onClose: () => void;
};

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  blockingSubscription,
  onClose,
}) => {
  const {
    submit: deleteAccount,
    isLoading,
    isError,
    errorKind,
    reset,
  } = useDeleteAccount();
  const logout = useLogout();

  const handleClose = useCallback(() => {
    if (isLoading) {
      return;
    }
    reset();
    onClose();
  }, [isLoading, onClose, reset]);

  const handleSignOut = useCallback(async () => {
    await logout();
    reset();
    onClose();
  }, [logout, onClose, reset]);

  const content = useMemo(() => {
    if (blockingSubscription && !isError) {
      return (
        <DeleteAccountSubscriptionWarning
          subscription={blockingSubscription}
          onCancel={handleClose}
        />
      );
    }

    if (isLoading) {
      return <DeleteAccountLoading />;
    }

    if (isError && errorKind) {
      return (
        <DeleteAccountError
          errorKind={errorKind}
          onRetry={deleteAccount}
          onSignOut={handleSignOut}
          onCancel={handleClose}
        />
      );
    }

    return (
      <DeleteAccountConfirm onSubmit={deleteAccount} onCancel={handleClose} />
    );
  }, [
    blockingSubscription,
    deleteAccount,
    errorKind,
    handleClose,
    handleSignOut,
    isError,
    isLoading,
  ]);

  return (
    <Dialog open={open} fullWidth onClose={handleClose}>
      {content}
    </Dialog>
  );
};
